// MIDAS V3 — Palette id registry (LEAF MODULE — keep it import-free)
//
// MIDAS-V3-PALETTES-TEAL-SLATE. Single source of truth for the set of V3
// palette ids. Four palettes ship: `claude` (default), `machineid`, `teal`,
// `slate`. Each id maps to the root class `p-<id>` whose token block lives in
// `components/v3/midas-v3-tokens.scss`.
//
// WHY THIS IS ITS OWN FILE (do not fold it back into midasV3Slice.ts):
// the body-portalled V3 surfaces (RailRowMenuV3, ModelPickerV3, ComposerMenuV3,
// ConfirmDialogV3, NewChatDialogV3, NewChatReadinessCluster) need
// `resolveV3PaletteClass()`. If they imported it from the slice they would pull
// in the slice's import chain (midasPageSlice → axios ESM), which CRA's jest
// transform cannot process — see `__tests__/staleModelHydration.test.ts`, which
// has to `jest.mock("../midasPageSlice")` for exactly that reason. This module
// imports NOTHING, so it is safe for any consumer. The slice imports FROM here.
//
// No MUI, no @emotion, no `sx`, no `dt` tokens.

export const MIDAS_V3_THEMES = ["claude", "machineid", "teal", "slate"] as const;
export type MidasV3Theme = (typeof MIDAS_V3_THEMES)[number];

export const isMidasV3Theme = (v: unknown): v is MidasV3Theme =>
  typeof v === "string" && (MIDAS_V3_THEMES as readonly string[]).includes(v);

/** Palette class carried by the LIVE V3 root (MidasV3Layout). Used by every
 *  body-portalled V3 surface so its wrapper matches the page. Falls back to
 *  `p-claude` when no V3 root is mounted.
 *
 *  Replaces the six copies of a 2-way DOM sniff (`.p-claude` present ?
 *  `p-claude` : `p-machineid`), which fell through to `p-machineid` — i.e.
 *  painted brand blue — under any palette other than claude. */
export const resolveV3PaletteClass = (): string => {
  if (typeof document === "undefined") return "p-claude";
  const roots = document.querySelectorAll(".midas-v3-root");
  for (let i = 0; i < roots.length; i += 1) {
    const cl = roots[i].classList;
    for (const t of MIDAS_V3_THEMES) {
      if (cl.contains(`p-${t}`)) return `p-${t}`;
    }
  }
  return "p-claude";
};
